import type { ComponentProps } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import type {
  BackgroundColorProps,
  BorderColorProps,
  BorderProps,
  BorderRadiusProps,
  BorderStyleProps,
  BorderWidthProps,
  MarginRightProps,
  OpacityProps,
} from 'styled-system';
import {
  backgroundColor,
  border,
  borderColor,
  borderRadius,
  borderStyle,
  borderWidth,
  marginRight,
  opacity,
  themeGet,
} from 'styled-system';

import type { CursorProps } from '../../theme/system-utilities';
import { cursor } from '../../theme/system-utilities';
import type { ArticleLabel } from '../../types/labels';
import Icon from '../Icon';
import { BaseLabel, baseLabelStyles, SmallBaseLabel } from '../Label';
import { getColorByType } from './utils';

const ICON_SIZES = 24;
const SMALL_ICON_SIZES = 18;

export const LabelWrapper = styled.div<
  BackgroundColorProps & BorderRadiusProps & BorderColorProps & BorderWidthProps & BorderStyleProps
>`
  ${backgroundColor};
  ${borderRadius};
  ${borderColor};
  ${borderWidth};
  ${borderStyle};
`;

const clickableLabelStateStyles = (props: { isClickable?: boolean; type?: ArticleLabel['type'] }) =>
  props.isClickable &&
  props.type &&
  css`
    &:hover {
      background-color: ${themeGet(getColorByType(props.type))(props)}1A;
      color: ${themeGet(getColorByType(props.type))(props)};
      > svg {
        fill: ${themeGet(getColorByType(props.type))(props)};
      }
    }

    &:active {
      background-color: ${themeGet('colors.sys.neutral.background.default')(props)};
    }

    &:focus-visible {
      background-color: ${themeGet(getColorByType(props.type))(props)}1A;
      color: ${themeGet(getColorByType(props.type))(props)};
      > svg {
        fill: ${themeGet(getColorByType(props.type))(props)};
      }
      outline: 2px solid ${themeGet('colors.sys.secondary.border.focus')(props)};
    }
  `;

export const Label = styled(BaseLabel)<
  OpacityProps & CursorProps & BorderProps & { isClickable?: boolean; type?: ArticleLabel['type'] }
>`
  ${opacity};
  ${cursor};
  ${border};
  ${props => clickableLabelStateStyles(props)};
`;

export const SmallLabel = styled(SmallBaseLabel)<
  OpacityProps & CursorProps & BorderProps & { isClickable?: boolean; type?: ArticleLabel['type'] }
>`
  ${opacity};
  ${cursor};
  ${border};
  ${props => clickableLabelStateStyles(props)};
`;

export const labelStyles: Partial<ComponentProps<typeof Label>> = {
  ...baseLabelStyles,
  px: ['sp_4', null, null, 'sp_8'],
};

export const InfoIcon = styled(Icon)<MarginRightProps>`
  ${marginRight};
`;

export const infoIconStyle: ComponentProps<typeof InfoIcon> = {
  size: ICON_SIZES,
  name: 'info',
  fill: 'sys.neutral.background.default',
  marginRight: '-5px',
};

export const smallInfoIconStyle: ComponentProps<typeof InfoIcon> = {
  ...infoIconStyle,
  size: SMALL_ICON_SIZES,
  marginRight: '-3px',
};
